/**
 * Página de solicitudes de unión al club.
 * Los directores pueden aprobar/rechazar solicitudes de miembros.
 *
 * También puede embeberse en otras vistas (p.ej. ClubDetailPage del admin de
 * asociación) pasando `clubId` explícito: la Cloud Function `approveJoinRequest`
 * ya autoriza a admin_union/admin_association además del director general.
 *
 * Las solicitudes que piden DIRIGIR un tipo de club salen además en
 * `SectionDirectorRequestsPage`, el buzón de la asociación.
 */

import { useEffect, useMemo, useState } from "react";
import TransferRequestsSection from "./components/TransferRequestsSection";
import { useAuthStore } from "@/stores/authStore";
import {
  joinRequestService,
  type JoinRequest,
} from "@/lib/services/joinRequests";
import { usePermissions } from "@/hooks/usePermissions";
import JoinRequestCard, {
  useJoinRequestActions,
} from "./components/JoinRequestCard";

export default function JoinRequestsPage({
  clubId: clubIdProp,
  embedded = false,
}: {
  /** Club a gestionar; por defecto el club del director en sesión. */
  clubId?: string;
  /** Sin header de página ni tip del dashboard (para incrustar en otra vista). */
  embedded?: boolean;
} = {}) {
  const { profile } = useAuthStore();
  const { directorOfSection } = usePermissions();
  const clubId = clubIdProp ?? profile?.directorOfClub?.id;

  const [allRequests, setAllRequests] = useState<JoinRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const { processingId, error, notice, setError, approve, reject } =
    useJoinRequestActions();

  // Filtro de solicitudes: cualquier director del club (general o de sección)
  // ve TODAS las solicitudes de miembro. Las solicitudes de "section_director"
  // se ocultan a directores de sección porque solo el director general o un
  // admin puede nombrar directores (aprobarlas daría permission-denied).
  const requests = useMemo(() => {
    if (!directorOfSection) return allRequests;
    return allRequests.filter((r) => r.requestedRole !== "section_director");
  }, [allRequests, directorOfSection]);

  /** Solicitudes reales que esta persona NO puede resolver, para no dejarla
   *  mirando una bandeja vacía sin saber que hay algo esperando a otro. */
  const ocultasPorPermiso = allRequests.length - requests.length;

  useEffect(() => {
    if (!clubId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const unsubscribe = joinRequestService.watchClubPendingRequests(
      clubId,
      (data) => {
        setAllRequests(data);
        setLoading(false);
      },
      (err) => {
        console.error("Error loading requests:", err);
        setError("Error al cargar las solicitudes");
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [clubId, setError]);

  if (!clubId) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4 flex items-center gap-3">
          <svg
            className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            No tienes un club asignado como director.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header — mismo patrón que OrganizationPage */}
      <div className={embedded ? "hidden" : undefined}>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30">
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
          <span className="flex items-center gap-2">
            Solicitudes de unión
            {!loading && requests.length > 0 && (
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary-600 text-white text-xs font-bold">
                {requests.length}
              </span>
            )}
          </span>
        </h1>
        <p className="text-gray-500 mt-1 ml-[52px]">
          Revisa y aprueba las solicitudes de nuevos miembros
        </p>
      </div>

      {/* F14: traslados entre clubes — solo aparece si hay alguno que mostrar */}
      <TransferRequestsSection clubId={clubId} />

      {error && (
        <div className="mb-5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center gap-3">
          <svg
            className="w-5 h-5 text-red-500 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {notice && (
        <div className="mb-5 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4 flex items-center gap-3">
          <svg
            className="w-5 h-5 text-emerald-500 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-sm text-emerald-700 dark:text-emerald-300">
            {notice}
          </p>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent" />
          <p className="text-sm text-gray-400 dark:text-gray-500">
            Cargando solicitudes…
          </p>
        </div>
      ) : requests.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center bg-white dark:bg-gray-800/50 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700">
          <div className="p-4 rounded-2xl bg-gray-100 dark:bg-gray-700/50 mb-4">
            <svg
              className="w-10 h-10 text-gray-400 dark:text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            Todo al día
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-xs">
            No hay solicitudes pendientes. Las nuevas solicitudes aparecen aquí
            en tiempo real.
          </p>
          {ocultasPorPermiso > 0 && (
            <div className="mt-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-left w-full max-w-sm text-sm text-amber-900">
              Hay{" "}
              <b>
                {ocultasPorPermiso} solicitud
                {ocultasPorPermiso !== 1 ? "es" : ""} para dirigir un tipo de
                club
              </b>{" "}
              en tu club. No aparece aquí porque la aprueba el{" "}
              <b>director/a general</b> o la Asociación — avísales para que la
              revisen.
            </div>
          )}
          <div
            className={`mt-5 px-5 py-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800/30 rounded-xl text-left w-full max-w-sm ${embedded ? "hidden" : ""}`}
          >
            <p className="text-xs font-semibold text-primary-700 dark:text-primary-300 mb-1">
              ¿Esperas miembros?
            </p>
            <p className="text-sm text-primary-600 dark:text-primary-400">
              Comparte el enlace de invitación de tu club. Lo encuentras en el{" "}
              <a href="/dashboard" className="underline font-medium">
                Dashboard
              </a>
              .
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((request) => (
            <JoinRequestCard
              key={request.id}
              request={request}
              processingId={processingId}
              onApprove={approve}
              onReject={reject}
            />
          ))}
        </div>
      )}
    </div>
  );
}
